<script lang="ts">
  import { selectedTeamStore, type TeamMember } from "$lib/stores/teams";
  import { makeApiRequest } from "$lib/utils/api";
  import { formatDistanceToNow } from "date-fns";

  import * as DropdownMenu from "$ui/dropdown-menu";
  import MembersForm from "$lib/components/identity/MembersForm.svelte";

  import * as AlertDialog from "$ui/alert-dialog";
  import * as Drawer from "$ui/drawer";
  import * as Table from "$ui/table";
  import { Button } from "$ui/button";
  import { LoadingSpinner } from "$ui/loading-spinner";
  import { SearchBar } from "$ui/search-bar";

  import { fuzzyMatch, isArrayEqual } from "$utils/helpers";
  import { ROLES } from "$utils/constants";

  import DotsIcon from "$assets/icons/dots.svelte";
  import EditIcon from "$assets/icons/edit.svelte";
  import BinIcon from "$assets/icons/bin.svelte";
  import SectionTitle from "$lib/components/shared/SectionTitle.svelte";

  let isLoading: boolean = true;
  let filteredMembers: TeamMember[] = [];

  let drawerOpen = false;
  let confirmModalOpen = false;
  let isDeleting: boolean = false;
  let errorMessage: string = "";
  let selectedMember: TeamMember | undefined = undefined;

  $: team = $selectedTeamStore;
  $: canEdit = team?.userClaims.includes("users:write");

  $: {
    filteredMembers = team?.members ?? [];
    filteredMembers.sort((a, b) => a.modified.valueOf() - b.modified.valueOf());
    isLoading = false;
  }

  const onAddMember = () => {
    selectedMember = undefined;
    drawerOpen = true;
  };

  const onEditMember = (email: string) => {
    selectedMember = team?.members.find((member) => member.email === email);
    drawerOpen = true;
  };

  const onDeleteConfirm = (email: string) => {
    selectedMember = team?.members.find((member) => member.email === email);
    confirmModalOpen = true;
  };

  const onDeleteMember = async () => {
    if (!selectedMember) {
      return;
    }
    try {
      isDeleting = true;
      await makeApiRequest(`/teams/${team?.id}/members/${selectedMember.email}`, {}, "DELETE");
      confirmModalOpen = false;
    } catch (error: any) {
      errorMessage = "Error deleting Member. " + error?.message;
      console.error("Error deleting Member:", error);
    } finally {
      isDeleting = false;
    }
  };

  const onSearch = (newTerm: string) => {
    filteredMembers =
      (newTerm !== ""
        ? team?.members.filter((member) => {
            const roleLabel = ROLES.find((role) => isArrayEqual(role.value, member.claims))?.label ?? "";
            console.log("member.email", member.email, "roleLabel", roleLabel);
            return fuzzyMatch(member.email, newTerm) || fuzzyMatch(roleLabel, newTerm);
          })
        : team?.members) ?? [];
  };
</script>

<SectionTitle>Members</SectionTitle>
<div class="flex flex-col-reverse xs:flex-row justify-between xs:items-center gap-4">
  <div class="w-full md:max-w-[300px]">
    <SearchBar onChange={onSearch} />
  </div>
  <Drawer.Root bind:open={drawerOpen}>
    <Drawer.Trigger disabled={!canEdit}>
      <Button on:click={onAddMember} class="w-full min-h-[46px]" disabled={!canEdit}>+ Add Members</Button>
    </Drawer.Trigger>
    <Drawer.Content>
      <Drawer.Header class="pt-6 px-6 pb-0 relative">
        <Drawer.Title class="text-xl">
          {selectedMember ? "Edit Member" : "Add New Members"}
        </Drawer.Title>
        <Drawer.Description class="p text-base">
          {selectedMember ? "Make changes to an Existing Member" : "Invite new members to join your team"}
        </Drawer.Description>
      </Drawer.Header>
      <div class="p-6 flex-1 flex flex-col">
        <MembersForm
          initialValues={selectedMember ? { members: [selectedMember] } : undefined}
          isEdit={selectedMember ? true : false}
          onSuccess={() => {
            drawerOpen = false;
          }}
        />
      </div>
    </Drawer.Content>
  </Drawer.Root>
</div>
{#if isLoading}
  <div class="flex justify-center items-center">
    <LoadingSpinner />
  </div>
{:else}
  <div class="card">
    <Table.Root>
      <Table.Header>
        <Table.Row class="hidden sm:table-row">
          <Table.Head class="font-semibold">User</Table.Head>
          <Table.Head class="font-semibold hidden xs:table-cell">Role</Table.Head>
          <Table.Head class="font-semibold hidden xs:table-cell">Last Modified</Table.Head>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {#if filteredMembers.length === 0}
          <Table.Row>
            <Table.Cell class="text-center py-8" colspan={4}>No members found</Table.Cell>
          </Table.Row>
        {/if}
        {#each filteredMembers as member (member.email)}
          <Table.Row class="py-2 sm:py-0">
            <Table.Cell data-label="User" class="font-medium truncate">
              {member.email}
            </Table.Cell>
            <Table.Cell data-label="Role">
              {ROLES.find((role) => isArrayEqual(role.value, member.claims))?.label ?? "-"}
            </Table.Cell>
            <Table.Cell data-label="Modified">{formatDistanceToNow(member.modified, { addSuffix: true })}</Table.Cell>
            <Table.Cell
              class="flex sm:justify-end !absolute sm:!relative top-1 -right-7 sm:top-auto sm:right-auto before:!hidden !w-auto"
            >
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild let:builder>
                  <Button variant="ghost" builders={[builder]} class="[&>svg]:rotate-90 max-h-[32px] px-3">
                    <DotsIcon />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content class="w-40" side="right" align="start" sideOffset={10}>
                  <DropdownMenu.Group>
                    <DropdownMenu.Item on:click={() => onEditMember(member.email)} Icon={EditIcon} disabled={!canEdit}>
                      Edit
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      class="!text-danger-400"
                      on:click={() => onDeleteConfirm(member.email)}
                      Icon={BinIcon}
                      disabled={!canEdit}
                    >
                      Delete
                    </DropdownMenu.Item>
                  </DropdownMenu.Group>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Table.Cell>
          </Table.Row>
        {/each}
      </Table.Body>
    </Table.Root>
  </div>
{/if}

<AlertDialog.Root
  open={confirmModalOpen}
  closeOnOutsideClick={true}
  onOpenChange={(isOpen) => {
    if (!isOpen) {
      confirmModalOpen = false;
      selectedMember = undefined;
    }
  }}
>
  <AlertDialog.Content>
    <AlertDialog.Header>
      <AlertDialog.Title>Are You Sure?</AlertDialog.Title>
      <AlertDialog.Description>
        You are about to delete member:
        <span class="font-medium">
          {selectedMember?.email}
        </span>
      </AlertDialog.Description>
    </AlertDialog.Header>
    {#if errorMessage}
      <div class="text-danger-400 text-sm">{errorMessage}</div>
    {/if}
    <AlertDialog.Footer>
      <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
      <Button variant="destructive" on:click={onDeleteMember} disabled={isDeleting}>
        {isDeleting ? "Deleting..." : "Delete Member"}
      </Button>
    </AlertDialog.Footer>
  </AlertDialog.Content>
</AlertDialog.Root>
